import type {
  CreateAnswerInput,
  CreateQuestionInput,
  QuestionResponse,
  UpdateAnswerInput,
  UpdateQuestionInput,
} from '@lms/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export function useLessonQuestions(lessonId: string) {
  return useQuery({
    queryKey: ['lessons', lessonId, 'questions'],
    queryFn: () => api.get<QuestionResponse[]>(`/api/lessons/${lessonId}/questions`),
    enabled: !!lessonId,
  })
}

export function useCreateQuestion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ lessonId, data }: { lessonId: string; data: CreateQuestionInput }) =>
      api.post(`/api/lessons/${lessonId}/questions`, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['lessons', variables.lessonId, 'questions'] })
    },
  })
}

export function useToggleUpvote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ lessonId, questionId }: { lessonId: string; questionId: string }) =>
      api.post(`/api/lessons/${lessonId}/questions/${questionId}/upvote`),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['lessons', variables.lessonId, 'questions'] })
    },
  })
}

export function useCreateAnswer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      lessonId,
      questionId,
      data,
    }: {
      lessonId: string
      questionId: string
      data: CreateAnswerInput
    }) => api.post(`/api/lessons/${lessonId}/questions/${questionId}/answers`, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['lessons', variables.lessonId, 'questions'] })
    },
  })
}

export function useUpdateQuestion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      lessonId,
      questionId,
      data,
    }: {
      lessonId: string
      questionId: string
      data: UpdateQuestionInput
    }) => api.patch(`/api/lessons/${lessonId}/questions/${questionId}`, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['lessons', variables.lessonId, 'questions'] })
    },
  })
}

export function useDeleteQuestion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ lessonId, questionId }: { lessonId: string; questionId: string }) =>
      api.delete(`/api/lessons/${lessonId}/questions/${questionId}`),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['lessons', variables.lessonId, 'questions'] })
    },
  })
}

export function useUpdateAnswer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      lessonId,
      questionId,
      answerId,
      data,
    }: {
      lessonId: string
      questionId: string
      answerId: string
      data: UpdateAnswerInput
    }) => api.patch(`/api/lessons/${lessonId}/questions/${questionId}/answers/${answerId}`, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['lessons', variables.lessonId, 'questions'] })
    },
  })
}

export function useDeleteAnswer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      lessonId,
      questionId,
      answerId,
    }: {
      lessonId: string
      questionId: string
      answerId: string
    }) => api.delete(`/api/lessons/${lessonId}/questions/${questionId}/answers/${answerId}`),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['lessons', variables.lessonId, 'questions'] })
    },
  })
}
